import { useLocation } from "react-router-dom";

interface JsonLdProps {
  type: "Organization" | "WebSite" | "WebPage" | "ContactPage" | "FAQPage";
  title?: string;
  description?: string;
  breadcrumbItems?: Array<{ name: string; url: string }>;
  faqData?: Array<{ question: string; answer: string }>;
}

const SITE_URL = "https://oplif.com";

export default function JsonLd({ type, title, description, breadcrumbItems, faqData }: JsonLdProps) {
  const location = useLocation();
  const pageUrl = `${SITE_URL}${location.pathname}`;

  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Oplif",
    url: SITE_URL,
    logo: `${SITE_URL}/favicon-512.png`,
    sameAs: [
      "https://twitter.com/Oplif",
    ],
    description:
      "The complete toolkit for restaurants, hotels, cafés and hospitality entrepreneurs. From permits to P&L — open smarter and run better.",
    email: "hello@oplif.com",
  };

  const websiteData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Oplif",
    url: SITE_URL,
    publisher: {
      "@type": "Organization",
      name: "Oplif",
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon-512.png` },
    },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  const webPageData = {
    "@context": "https://schema.org",
    "@type": type === "FAQPage" ? "FAQPage" : "WebPage",
    name: title || "Oplif",
    description: description || "Oplif hospitality operating system.",
    url: pageUrl,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": pageUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "Oplif",
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon-512.png` },
    },
    ...(faqData && faqData.length > 0
      ? {
          mainEntity: faqData.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        }
      : {}),
  };

  const contactPageData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: title || "Contact Oplif",
    description: description || "Get in touch with the Oplif team.",
    url: pageUrl,
    publisher: {
      "@type": "Organization",
      name: "Oplif",
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon-512.png` },
    },
  };

  const breadcrumbData = breadcrumbItems?.length
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbItems.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
        })),
      }
    : null;

  const getData = () => {
    switch (type) {
      case "Organization":
        return organizationData;
      case "WebSite":
        return websiteData;
      case "ContactPage":
        return contactPageData;
      default:
        return webPageData;
    }
  };

  const data = getData();

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
      {breadcrumbData && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbData)}</script>
      )}
    </>
  );
}
